import { useState } from 'react';
import { buildDocumentGroups, type DocVersionFile, type DocumentGroup } from '../lib/documentVersions.ts';
import { formatFileSize } from '../lib/fileHistory.ts';
import type { ReplyRemoval } from '../lib/replyRemoval.ts';
import { ExpiryChip } from './ExpiryChip.tsx';
import { ReplyRemoveToggle, WillBeRemovedNote } from './ReplyRemoveToggle.tsx';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function FileIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4D148C" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function VersionHistory({ group }: { group: DocumentGroup }) {
  return (
    <div className="bg-slate-50 px-4 pb-3 pl-11 pt-1">
      <div className="py-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
        Version history
      </div>
      {group.versions.map((v) => (
        <div key={v.id} className="flex items-center gap-2.5 border-t border-slate-200 py-2">
          <span className={`w-8 shrink-0 rounded py-1 text-center text-[11px] font-bold ${
            v.state === 'current' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400'
          }`}>v{v.version}</span>
          <div className="min-w-0 flex-1 text-xs">
            <span className={v.state === 'current' ? 'font-semibold' : 'font-semibold text-slate-500'}>{v.file_name}</span>
            <span className="text-slate-400">
              {' · '}{v.state !== 'current' && v.superseded_at
                ? `${v.state === 'removed' ? 'removed' : 'replaced'} ${formatDate(v.superseded_at)}`
                : formatDate(v.created_at)}
            </span>
          </div>
          {v.download_url && (
            <a href={v.download_url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs text-[#4D148C] hover:underline">Download</a>
          )}
          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
            v.state === 'current' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-500'
          }`}>{v.state === 'current' ? 'Current' : v.state === 'removed' ? 'Removed' : 'Superseded'}</span>
        </div>
      ))}
    </div>
  );
}

// Literal class strings so Tailwind's scanner emits every grid. The *_REMOVE
// variants add the Remove / Undo column (vendor, while a reply can carry it).
const GRID_WITH_EXPIRY = 'grid-cols-[1fr_118px_128px_36px]';
const GRID_NO_EXPIRY = 'grid-cols-[1fr_128px_36px]';
const GRID_WITH_EXPIRY_REMOVE = 'grid-cols-[1fr_118px_128px_76px_36px]';
const GRID_NO_EXPIRY_REMOVE = 'grid-cols-[1fr_128px_76px_36px]';

export function DocumentTable({
  files,
  requiresExpiry,
  initialOpenId = null,
  removal,
}: {
  files: DocVersionFile[];
  /** The owning question is flagged requires_expiry. */
  requiresExpiry: boolean;
  /** Test seam: the group (by current file id) whose version history starts open. */
  initialOpenId?: string | null;
  /**
   * Vendor reply removal (lib/replyRemoval.ts): each current document gets a
   * Remove / Undo toggle, sent with the next thread reply. Omitted = no column.
   */
  removal?: ReplyRemoval;
}) {
  const [openId, setOpenId] = useState<string | null>(initialOpenId);
  const groups = buildDocumentGroups(files);
  if (groups.length === 0) return null;

  // Same rule as the admin showExpiryButton: no Expiry column on a question
  // that doesn't collect one, but never hide a date a current file already
  // carries (it still drives the expiry-reminder rollup).
  const showExpiry = requiresExpiry || groups.some((g) => g.current.expires_at != null);
  const gridCols = removal
    ? showExpiry ? GRID_WITH_EXPIRY_REMOVE : GRID_NO_EXPIRY_REMOVE
    : showExpiry ? GRID_WITH_EXPIRY : GRID_NO_EXPIRY;

  return (
    <div className="overflow-hidden rounded-[10px] border border-slate-200">
      <div className={`grid ${gridCols} border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500`}>
        <div className="px-3.5 py-2.5">Document</div>
        {showExpiry && <div className="px-2 py-2.5">Expiry</div>}
        <div className="px-2 py-2.5">Status</div>
        {removal && <div />}
        <div />
      </div>
      {groups.map((g) => {
        const open = openId === g.current.id;
        const multi = g.versions.length > 1;
        const isCurrent = g.current.superseded_at === null;
        const removing = !!removal && isCurrent && removal.isRemoving(g.current.id);
        const toggleOpen = () => multi && setOpenId(open ? null : g.current.id);
        return (
          <div key={g.current.id} className="border-t border-slate-200 first:border-t-0">
            {/* A div, not a <button>: the row holds a link and the Remove button,
                and interactive content cannot nest inside a button. Keyboard users
                expand the history with the chevron button. */}
            <div
              onClick={toggleOpen}
              className={`grid w-full ${gridCols} items-center text-left ${multi ? 'cursor-pointer' : 'cursor-default'} ${removing ? 'bg-red-50/60' : ''}`}
            >
              <span className="flex min-w-0 items-center gap-2.5 px-3.5 py-3">
                <FileIcon />
                <span className="min-w-0">
                  {g.current.download_url ? (
                    <a href={g.current.download_url} target="_blank" rel="noopener noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       className={`block truncate text-[13px] font-semibold text-[#4D148C] hover:underline ${removing ? 'line-through opacity-60' : ''}`}>
                      {g.current.file_name}
                    </a>
                  ) : (
                    <span className={`block truncate text-[13px] font-semibold text-[#4D148C] ${removing ? 'line-through opacity-60' : ''}`}>{g.current.file_name}</span>
                  )}
                  {removing ? (
                    <WillBeRemovedNote />
                  ) : (
                    <span className="text-[11px] text-slate-400">
                      {g.versions.length} version{g.versions.length === 1 ? '' : 's'}
                      {g.current.size_bytes != null ? ` · ${formatFileSize(g.current.size_bytes)}` : ''}
                    </span>
                  )}
                </span>
              </span>
              {showExpiry && (
                <span className="px-2 py-3 text-[13px] text-slate-700">
                  {g.current.expires_at ? <ExpiryChip expiresAt={g.current.expires_at} /> : '—'}
                </span>
              )}
              <span className="px-2 py-3">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                  g.current.superseded_at === null ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-500'
                }`}>
                  {g.current.superseded_at === null
                    ? 'Current'
                    : g.current.removed_at
                      ? 'Removed'
                      : 'Superseded'}
                </span>
              </span>
              {removal && (
                <span className="flex justify-end py-3 pl-1">
                  {isCurrent && (
                    <ReplyRemoveToggle
                      fileName={g.current.file_name}
                      removing={removing}
                      onToggle={() => removal.onToggle(g.current.id)}
                      disabled={removal.disabled ?? false}
                    />
                  )}
                </span>
              )}
              <span className="flex justify-center px-2 py-3 text-slate-400">
                {multi && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOpen();
                    }}
                    aria-expanded={open}
                    aria-label={`${open ? 'Hide' : 'Show'} version history for ${g.current.file_name}`}
                    className="rounded p-0.5 hover:bg-slate-100"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round"
                         className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                )}
              </span>
            </div>
            {open && <VersionHistory group={g} />}
          </div>
        );
      })}
    </div>
  );
}
